import { ProjectPageFragment } from "@/__generated__/graphql";
import { ComponentProps } from "@components/ComponentProps";
import Hero, { CreateHeroProps } from "@components/Hero";
import InformationSection from "@components/InformationSection";
import IconLegend, { IconLegendItem } from "@components/IconLegend";
import { slugify } from "@/lib/utils";
import PageMeta from "@components/PageMeta";
import PreviewEditLink from "@components/PreviewEditLink";
import Slices from "@components/slices";
import SocialShareBlock from "@components/SocialShareBlock";
import { WindowWidth } from "@lib/hooks/WindowWidth";
import { useMobile } from "@lib/hooks/useMobile";
import { GlobalPageProps } from "@lib/utils";
import { GridContainer, GridWrapper } from "@theme/lib";
import { ReactElement } from "react";

export type ProjectPageProps = ComponentProps<
  ProjectPageFragment
>;

export default function ProjectPage({ id, previewEditUrl, latestVersion, versionStatus, published, path, metatags, breadcrumbs, title, content, hero, informationSection, socialLinks, pageUrl }: ProjectPageProps & GlobalPageProps): ReactElement<any> {
  const heroProps = hero ? CreateHeroProps(hero) : null;

  const is1080 = useMobile(1080);
  const is960 = useMobile(960);
  const isMobile = useMobile(WindowWidth.Mobile);

  const iconOrder = [
    'project-status',
    'project-started',
    'project-ended',
    'habitat',
    'region/country',
    'contact-details',
    'social-links',
  ];

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={breadcrumbs}
        path={path}
      />
      {previewEditUrl &&
        <PreviewEditLink url={previewEditUrl} latestVersion={latestVersion} versionStatus={versionStatus} published={published} pageUrl={pageUrl}/>
      }
      {heroProps && <Hero {...heroProps} />}
      <main>
        {informationSection?.tiles?.length &&
          <GridContainer>
            <GridWrapper alignment={"full-width"}>
              <IconLegend
                toggle={isMobile}
                toggleItem="project-status"
                toggleLabel="Project information"
                grid={isMobile ? 1 : is960 ? 3 : is1080 ? 4 : 5}
                items={[...informationSection.tiles]
                  .sort((a, b) => {
                    const aIndex = iconOrder.indexOf(slugify(a.label));
                    const bIndex = iconOrder.indexOf(slugify(b.label));
                    return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
                  })
                  .flatMap(({ label, value }) => [{
                    type: '' as IconLegendItem['type'],
                    label,
                    info: value ? `<div class="legend-item-info">${value}</div>` : undefined,
                  }])}
              />
            </GridWrapper>
          </GridContainer>}
        {informationSection?.people &&
          <GridContainer>
            <GridWrapper alignment={"full-width"}>
              <InformationSection people={informationSection.people} />
            </GridWrapper>
          </GridContainer>}
        {content && <Slices {...content} />}
        {(socialLinks?.linkedin || socialLinks?.facebook || socialLinks?.twitter) && (
          <GridContainer><SocialShareBlock pageUrl={pageUrl} {...socialLinks} /></GridContainer>
        )}
      </main>
    </>
  );
}
